import React from "react";
import { useFormContext, useFormState, useWatch } from "@imminently/decisively-ui/Form";
import { get } from "lodash";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import GoalMenu from "@/pages/models/release/GoalMenu";
import { ParsedGraphContextProvider, useParsedGraph } from "@/pages/models/release/GraphContext";
import { AttributeInfoFormValues } from "./useAttributeForm";
import TextField from "@imminently/decisively-ui/TextField";
import SelectField from "@imminently/decisively-ui/SelectField";
import CheckboxField from "@imminently/decisively-ui/CheckboxField";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import { useTranslation } from "react-i18next";

type SelectControlProps = {
  name: string;
  label: string;
};

const SelectRequestMapping: React.FC<SelectControlProps> = ({ name, label }) => {
  const release = useFullRelease();
  const mappings = (release?.mapping ?? [])
    .filter((it) => it.type === "request")
    .map((it) => ({ label: it.name, value: it.id }));

  const hasData = mappings.length > 0;
  const placeholder = hasData ? label : "No mappings available";

  return (
    <SelectField
      name={name}
      label={placeholder}
      options={mappings}
      disabled={!hasData}
      fullWidth
    />
  );
};

const SelectConnection: React.FC<SelectControlProps> = ({ name, label }) => {
  const release = useFullRelease();
  const connections = (release?.connections ?? []).map((it) => ({ label: it.name, value: it.id }));

  const hasData = connections.length > 0;
  const placeholder = hasData ? label : "No connections available";

  return (
    <SelectField
      name={name}
      label={placeholder}
      options={connections}
      disabled={!hasData}
      fullWidth
    />
  );
};

interface SegmentValue {
  type: string;
  value: string;
  useDescriptions: boolean;
}

type EnrichmentSegmentProps = {
  name: string;
  // value: SegmentValue;
  typeOptions: Array<{ key: string; value: string }>;
  hideUseDescription?: boolean;
  control: React.ComponentType<{ name: string; type: string }>;
};

const EnrichmentSegment: React.FC<EnrichmentSegmentProps> = (p) => {
  const { name, typeOptions, hideUseDescription = false, control: Control } = p;
  const form = useFormContext<AttributeInfoFormValues>();
  const values = useWatch({ control: form.control });

  const type = get(values, `${name}.type`, typeOptions[0].value);

  const typeOptionsFormatted = typeOptions.map((t) => ({ value: t.value, label: t.key }));

  return (
    <div className="flex flex-col gap-2 w-full">
      <SelectField
        name={`${name}.type`}
        label="Type"
        options={typeOptionsFormatted}
        fullWidth
      />
      <Control
        name={`${name}.value`}
        type={type}
      />
      {hideUseDescription ? null : (
        <CheckboxField
          name={`${name}.useDescriptions`}
          label="Use descriptions"
        />
      )}
    </div>
  );
};

const ConnectionControl = ({ name, type }: any) => {
  const form = useFormContext<AttributeInfoFormValues>();
  const values = useWatch({ control: form.control });
  const formState = useFormState();
  const errors = (formState?.errors as any) || {};
  const touched = (formState?.touchedFields as any) || {};
  const setFieldValue = (field: string, value: any) =>
    form.setValue(field as any, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  const graph = useParsedGraph();

  const value = get(values, name);
  const setValue = (v) => setFieldValue(name, v);
  const error = (touched[name] && errors[name]) || false;

  if (type === "attribute") {
    return (
      <div className="flex flex-col">
        <GoalMenu
          graph={graph}
          goalId={value}
          onGoalIdChange={setValue}
          allowEmpty
        />
        {Boolean(error) ? (
          <FormHelperText
            error
            style={{ margin: "0 1rem", fontStyle: "italic" }}
          >
            {error}
          </FormHelperText>
        ) : null}
      </div>
    );
  }

  if (type === "constant") {
    return (
      <SelectConnection
        name={name}
        label="Value"
      />
    );
  }

  return null;
};

const MappingControl = ({ name, type }: any) => {
  const form = useFormContext<AttributeInfoFormValues>();
  const values = useWatch({ control: form.control });
  const setFieldValue = (field: string, value: any) =>
    form.setValue(field as any, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  const graph = useParsedGraph();

  const value = get(values, name);
  const setValue = (v) => setFieldValue(name, v);

  if (type === "rule" || type === "goal") {
    return (
      <GoalMenu
        graph={graph}
        goalId={value}
        onGoalIdChange={setValue}
        allowEmpty
      />
    );
  }

  if (type === "file") {
    return (
      <SelectRequestMapping
        name={name}
        label="Value"
      />
    );
  }

  return null;
};

export const AttributeEnrichmentForm = ({ scope }: { scope: string }) => {
  const { t } = useTranslation();
  const scopedValue = (name: string) => `${scope}.${name}`;

  return (
    <ParsedGraphContextProvider>
      <FormPanel>
        <FormPanel.Title>{t("flow.enrichment")}</FormPanel.Title>
        <FormPanel>
          <Typography>
            Enrichments are a way to trigger an external API when the attribute is determined to have a value.
          </Typography>
          <TextField
            label="Title"
            name={scopedValue("title")}
            fullWidth
          />
        </FormPanel>
      </FormPanel>

      <FormPanel>
        <FormPanel.Title>Connection</FormPanel.Title>
        <FormPanel>
          <EnrichmentSegment
            hideUseDescription
            name={scopedValue("connection")}
            control={ConnectionControl}
            typeOptions={[
              { key: "Constant", value: "constant" },
              { key: "Attribute", value: "attribute" },
            ]}
          />
        </FormPanel>
      </FormPanel>

      <FormPanel>
        <FormPanel.Title>Mapping</FormPanel.Title>
        <FormPanel>
          <div className="flex flex-col gap-2">
            <Typography variant="h6">Outbound</Typography>
            <EnrichmentSegment
              name={scopedValue("mapping.outbound")}
              control={MappingControl}
              typeOptions={[
                { key: "Rule", value: "rule" },
                { key: "Goal", value: "goal" },
                { key: "File", value: "file" },
                { key: "Default", value: "default" },
              ]}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography variant="h6">Inbound</Typography>
            <EnrichmentSegment
              name={scopedValue("mapping.inbound")}
              control={MappingControl}
              typeOptions={[
                { key: "Rule", value: "rule" },
                { key: "Goal", value: "goal" },
                { key: "File", value: "file" },
              ]}
            />
          </div>
        </FormPanel>
      </FormPanel>

      <FormPanel>
        <FormPanel.Title>Transform</FormPanel.Title>
        <FormPanel>
          <div className="flex flex-col gap-2">
            <Typography variant="h6">Outbound</Typography>
            <EnrichmentSegment
              name={scopedValue("transform.outbound")}
              control={MappingControl}
              typeOptions={[
                { key: "Rule", value: "rule" },
                { key: "Goal", value: "goal" },
                { key: "File", value: "file" },
              ]}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography variant="h6">Inbound</Typography>
            <EnrichmentSegment
              name={scopedValue("transform.inbound")}
              control={MappingControl}
              typeOptions={[
                { key: "Rule", value: "rule" },
                { key: "Goal", value: "goal" },
                { key: "File", value: "file" },
              ]}
            />
          </div>
        </FormPanel>
      </FormPanel>
    </ParsedGraphContextProvider>
  );
};
